import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Button, Card, CardContent, Chip, Divider, List, ListItem, ListItemText, Snackbar, Stack, Switch, Typography,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import PageHeader from '../components/PageHeader';
import StateBoundary from '../components/StateBoundary';
import { useSettingsViewModel } from '../viewmodels/SettingsViewModel';

function limitRows(l) {
  return [
    ['Messages you can send to the team', `${l.messages_per_hour} per hour`],
    ['Automatic scheduled runs', `${l.scheduled_runs_per_day} per day`],
    ['Steps in one team round', `${l.max_steps_per_round}`],
    ['Time for one team round', `${l.max_seconds_per_round} seconds`],
    ['Messages between team members in one round', `${l.max_bot_messages_per_round}`],
    ['A proposal waits for your approval for', `${l.approval_expiry_hours} hours`],
  ];
}

export default function SettingsView() {
  const vm = useSettingsViewModel();
  const s = vm.settings;
  const wa = s?.whatsapp;
  const canSwitch = Boolean(s?.can_edit) && !vm.saving && (wa?.sending_enabled || wa?.connected);

  return (
    <>
      <PageHeader title="Settings" subtitle="Permissions you control for your team, and the limits that keep usage under control." />
      <StateBoundary loading={vm.loading} error={vm.error} onRetry={vm.reload}>
        {s && (
          <Stack spacing={2.5}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <WhatsAppIcon color="success" />
                  <Typography variant="h6">WhatsApp messages to customers</Typography>
                  <Chip size="small" color={wa.connected ? 'success' : 'default'} variant={wa.connected ? 'filled' : 'outlined'} label={wa.connected ? 'WhatsApp connected' : 'WhatsApp not connected'} />
                </Stack>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  This is your permission for your team to propose WhatsApp messages to customers. Switching it on does not let anyone
                  send on their own: every message still needs your approval, and each team member also needs the
                  &quot;Send WhatsApp messages&quot; access on the Team page.
                </Typography>
                {!wa.sending_available && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Real WhatsApp sending is not active in this version of OrkaCrew yet. Your choice is saved and will apply when it is turned on.
                    Nothing is sent to customers today.
                  </Alert>
                )}
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <div>
                    <Typography fontWeight={600}>Allow my team to propose WhatsApp messages</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {wa.sending_enabled ? 'On. Your team may propose messages for your approval.' : 'Off. Your team cannot propose WhatsApp messages.'}
                    </Typography>
                  </div>
                  <Switch
                    checked={wa.sending_enabled}
                    disabled={!canSwitch}
                    onChange={(e) => vm.setWhatsappSending(e.target.checked)}
                    slotProps={{ input: { 'aria-label': 'Allow my team to propose WhatsApp messages' } }}
                  />
                </Stack>
                {!wa.connected && (
                  <Alert
                    severity="warning"
                    sx={{ mt: 2 }}
                    action={<Button component={RouterLink} to="/connections" color="inherit" size="small">Connections</Button>}
                  >
                    Connect your WhatsApp account first, then you can switch this on.
                  </Alert>
                )}
                {!s.can_edit && <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Only the account owner can change this.</Typography>}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <ShieldOutlinedIcon color="primary" />
                  <Typography variant="h6">Always in force</Typography>
                </Stack>
                <List dense disablePadding>
                  {[
                    'Nothing leaves your business, and no team member is added, until you approve it.',
                    'New team members start with no access. You choose what each one can use.',
                    'Team members can only message people allowed by the reporting lines.',
                    'Store data and messages from your team can never change permissions or limits.',
                  ].map((t) => (
                    <ListItem key={t} disableGutters><ListItemText primary={t} /></ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Usage limits</Typography>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  Your team uses your own AI key, so these platform limits keep a round from running away. They apply to your whole account.
                </Typography>
                {limitRows(s.limits).map(([label, value], i, all) => (
                  <React.Fragment key={label}>
                    <Stack direction="row" justifyContent="space-between" sx={{ py: 1.25 }}>
                      <Typography>{label}</Typography>
                      <Typography fontWeight={600}>{value}</Typography>
                    </Stack>
                    {i < all.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </CardContent>
            </Card>
          </Stack>
        )}
      </StateBoundary>
      <Snackbar open={Boolean(vm.feedback)} autoHideDuration={6000} onClose={vm.clearFeedback} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {vm.feedback ? <Alert severity={vm.feedback.severity} onClose={vm.clearFeedback}>{vm.feedback.text}</Alert> : undefined}
      </Snackbar>
    </>
  );
}
